"use client";

import { useState } from 'react';
import { ChevronDownIcon, CircleStackIcon, ShieldCheckIcon, ChartBarIcon, CubeIcon } from '@heroicons/react/24/outline';

interface SolutionArea {
  id: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  whatWeBuild: string;
  outcomes: string[];
}

const solutionAreas: SolutionArea[] = [
  {
    id: 'solution_1',
    icon: CircleStackIcon,
    title: 'Batch-Level Traceability Warehouse',
    description: 'Complete visibility into every batch from manufacturing through distribution to end customer.',
    whatWeBuild: 'We build a comprehensive batch tracking system that integrates manufacturing execution systems, warehouse management, and distribution data into a unified traceability platform.',
    outcomes: [
      'Complete batch lineage tracking across all systems',
      'Instant recall readiness with full product location visibility',
      'Automated expiry alerts at batch and SKU level',
      'Integration with ERP, WMS, and logistics systems'
    ]
  },
  {
    id: 'solution_2',
    icon: ShieldCheckIcon,
    title: 'Regulatory Compliance Dashboard',
    description: 'Automated compliance monitoring and reporting for regulatory requirements.',
    whatWeBuild: 'We create an audit-ready compliance system with automated report generation, complete audit trails, and real-time compliance monitoring across all regulatory requirements.',
    outcomes: [
      'Automated regulatory reporting (FDA, EMA, local authorities)',
      'Complete audit trails for all data transactions',
      'Real-time compliance monitoring and alerts',
      'Deviation tracking and CAPA management integration'
    ]
  },
  {
    id: 'solution_3',
    icon: ChartBarIcon,
    title: 'Distribution Network Analytics',
    description: 'Real-time visibility into distributor performance, territory analysis, and channel profitability.',
    whatWeBuild: 'We develop a comprehensive distribution intelligence platform that consolidates data from all distributors, territories, and sales channels into actionable insights.',
    outcomes: [
      'Distributor performance tracking and scorecards',
      'Territory-level sales and inventory analysis',
      'Demand forecasting by region and product',
      'Channel profitability and margin analysis'
    ]
  },
  {
    id: 'solution_4',
    icon: CubeIcon,
    title: 'Inventory Optimization Engine',
    description: 'Intelligent inventory management with expiry risk detection and reorder optimization.',
    whatWeBuild: 'We implement an AI-powered inventory optimization system that analyzes historical patterns, demand forecasts, and expiry risks to optimize stock levels across the network.',
    outcomes: [
      'Slow-moving stock detection and alerts',
      'Expiry risk analysis with proactive recommendations',
      'Automated reorder point optimization',
      'Inventory efficiency metrics and KPIs'
    ]
  }
];

export default function SimaxPharmaEDWSolutionSection() {
  const [openId, setOpenId] = useState<string | null>('solution_1');

  const toggleSolution = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="py-14 sm:py-20 lg:py-24 bg-navy px-4 sm:px-6 lg:px-8 xl:px-12 w-full md:min-h-screen md:flex md:items-center">
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-white text-2xl sm:text-3xl md:text-4xl xl:text-[3rem] leading-[1.2] font-bold mb-6">
            Our Pharma-Specific <span className="italic">EDW Solution</span>
          </h2>
          <p className="text-white/80 text-lg leading-[1.7] max-w-3xl mx-auto">
            Four integrated solution areas designed specifically for pharmaceutical data challenges.
          </p>
        </div>

        {/* Expandable Solution Areas */}
        <div className="space-y-4">
          {solutionAreas.map((solution) => {
            const IconComponent = solution.icon;
            const isOpen = openId === solution.id;
            return (
              <div
                key={solution.id}
                className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden transition-all hover:border-gold/30"
              >
                <button
                  onClick={() => toggleSolution(solution.id)}
                  className="w-full px-8 py-6 flex items-center justify-between gap-4 text-left hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/20 flex items-center justify-center text-gold flex-shrink-0">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-serif font-bold text-white mb-1">
                        {solution.title}
                      </h3>
                      <p className="text-white/70 text-sm">{solution.description}</p>
                    </div>
                  </div>
                  <div
                    className={`transform transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  >
                    <ChevronDownIcon className="w-6 h-6 text-gold" />
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? 'max-h-[600px]' : 'max-h-0'
                  }`}
                >
                  <div className="px-8 pb-6 pt-2 pl-[88px] border-t border-white/10">
                    {/* What We Build */}
                    <div className="mb-6 mt-4">
                      <h4 className="text-gold text-sm font-semibold uppercase tracking-wider mb-2">
                        What We Build
                      </h4>
                      <p className="text-white/80 text-base leading-[1.6]">
                        {solution.whatWeBuild}
                      </p>
                    </div>

                    {/* Outcomes */}
                    <div>
                      <h4 className="text-gold text-sm font-semibold uppercase tracking-wider mb-3">
                        Outcomes & Insights
                      </h4>
                      <ul className="space-y-2">
                        {solution.outcomes.map((outcome, index) => (
                          <li key={index} className="flex items-start text-white/80 text-sm leading-[1.6]">
                            <span className="text-gold mr-3 flex-shrink-0 mt-1">◦</span>
                            <span>{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}